import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import axiosInstance from '../apiServices/axios';
import { DOC_PROJECT, SAS_API, PROJECT_ADD_INVESTOR, NDA_DOC, COMPANY_SEARCH, COMPANY_DETAIL, COMPANY_MERGED_PROFILE, CATEGORIZED_TASKS, DUPLICATE_PROJECT, DUPLICATE_PROJECT_JOB } from '../apiServices/api_routes';
import { capitalizeFirstLetter, isObject } from '../utils/commonUtils';
import { isActiveDuplicateJob, snapshotSourceProject } from '../utils/duplicateProjectJobs';
import { toast } from 'react-toastify';
import store from './store';

export const GetProjectData = createAsyncThunk('projectData/getProjectData', async (_, { getState }) => {
    const workspaceId = getState()?.user?.activeWorkspaceId || localStorage.getItem('active_workspace_id');
    const url = workspaceId ? `${DOC_PROJECT}?workspace_id=${workspaceId}` : DOC_PROJECT;
    const data = await axiosInstance.get(url);
    return data;
});
export const PostProjectData = createAsyncThunk('projectData/postProjectData', async (arg, { rejectWithValue }) => {
    try {
        const workspaceId = store?.getState()?.user?.activeWorkspaceId || localStorage.getItem('active_workspace_id');
        const url = workspaceId ? `${DOC_PROJECT}?workspace_id=${workspaceId}` : DOC_PROJECT;
        const data = await axiosInstance.post(url, arg.body);
        if (typeof arg.callback === 'function') {
            arg.callback();
        }
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});
export const PatchProjectData = createAsyncThunk('projectData/patchProjectData', async (body, { rejectWithValue }) => {
    if (!body?.id) {
        return rejectWithValue({ detail: 'Project id is required.' });
    }
    try {
        const data = await axiosInstance.patch(DOC_PROJECT + body.id + '/', body?.data);
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});

export const GetCurrentProjectData = createAsyncThunk('projectData/getCurrentProjectData', async () => {
    const data = await axiosInstance.get(DOC_PROJECT + localStorage['project_id']);
    return data;
});

export const UpdateProjectProgress = createAsyncThunk('projectData/updateProjectProgress', async () => {
    const data = await axiosInstance.get(DOC_PROJECT + localStorage['project_id']);
    return data;
});
export const DeleteProjectData = createAsyncThunk('DELETE_DOC_PROJECT', async ({ id }) => {
    const data = await axiosInstance.delete(DOC_PROJECT + id);
    return data;
});

export const DuplicateProjectData = createAsyncThunk(
    'projectData/duplicateProjectData',
    async ({ id, projectName, sourceProject }, { dispatch, rejectWithValue }) => {
        try {
            const body = projectName ? { project_name: projectName } : {};
            const response = await axiosInstance.post(DUPLICATE_PROJECT(id), body);
            dispatch(
                registerDuplicateJob({
                    jobId: response.data.job_id,
                    sourceProjectId: response.data.source_project_id || id,
                    projectName: response.data.project_name || projectName,
                    status: response.data.status || 'pending',
                    sourceProjectSnapshot: snapshotSourceProject(sourceProject),
                })
            );
            toast.info("Your project is being duplicated. We'll notify you once it's done.");
            return response.data;
        } catch (error) {
            if (error.response?.status === 409 && error.response?.data?.job_id) {
                dispatch(
                    registerDuplicateJob({
                        jobId: error.response.data.job_id,
                        sourceProjectId: error.response.data.source_project_id || id,
                        projectName: error.response.data.project_name || projectName,
                        status: error.response.data.status || 'pending',
                        sourceProjectSnapshot: snapshotSourceProject(sourceProject),
                    })
                );
                toast.info("Your project is being duplicated. We'll notify you once it's done.");
                return error.response.data;
            }
            return rejectWithValue(error.response?.data || { detail: 'Project duplication failed.' });
        }
    }
);

export const pollDuplicateProjectJobs = createAsyncThunk(
    'projectData/pollDuplicateProjectJobs',
    async (_, { getState, dispatch, rejectWithValue }) => {
        const jobs = (getState().projectData.duplicateJobs ?? []).filter(isActiveDuplicateJob);
        if (!jobs.length) return [];

        const results = [];
        for (const job of jobs) {
            try {
                const response = await axiosInstance.get(DUPLICATE_PROJECT_JOB(job.jobId));
                const payload = response.data;
                results.push(payload);

                if (payload.status === 'completed') {
                    dispatch(
                        completeDuplicateJob({
                            jobId: job.jobId,
                            projectName: payload.project_name || job.projectName,
                        })
                    );
                    await dispatch(GetProjectData());
                    toast.success(
                        payload.project_name
                            ? `${payload.project_name} is ready`
                            : 'Project duplication successful'
                    );
                } else if (payload.status === 'failed') {
                    dispatch(removeDuplicateJob({ jobId: job.jobId }));
                    toast.error(payload.error_message || 'Project duplication failed.');
                } else {
                    dispatch(
                        updateDuplicateJobStatus({
                            jobId: job.jobId,
                            status: payload.status,
                        })
                    );
                }
            } catch (error) {
                return rejectWithValue(error.response?.data || { detail: 'Unable to check duplicate status.' });
            }
        }
        return results;
    }
);

export const FetchLogo = createAsyncThunk('projectData/Logo', async (arg, { getState }) => {
    const logo = await getState()?.projectData?.selectedProject?.logo;
    if (logo) {
        return await axiosInstance.post(SAS_API, null, {
            params: { ProjectImg: logo },
        });
    }
});
export const AddNda = createAsyncThunk('POST_NDA_DOC', async (body, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post(NDA_DOC + localStorage['project_id'] + '/', body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});

export const DeleteNda = createAsyncThunk('DELETE_NDA_DOC', async (id, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.delete(NDA_DOC + localStorage['project_id'] + '/' + id + '/');
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});
export const GetCompanyId = createAsyncThunk('GET_COMPANY_SEARCH', async (website, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.get(COMPANY_SEARCH + website);
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});
export const GetCompanyDetail = createAsyncThunk('GET_COMPANY_DETAIL', async (id, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.get(COMPANY_DETAIL + id);
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});

/** CoreSignal + custom enrichment merged on the server (project creation wizard). */
export const GetMergedCompanyProfile = createAsyncThunk('GET_MERGED_COMPANY_PROFILE', async (arg, { rejectWithValue }) => {
    try {
        const website = typeof arg === 'string' ? arg : arg?.website;
        const companyName = typeof arg === 'object' ? arg?.company_name : undefined;
        const params = new URLSearchParams({ website_url: website });
        if (companyName) {
            params.set('company_name', companyName);
        }
        const data = await axiosInstance.get(`tools/company_automation/merged_profile/?${params.toString()}`);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data);
    }
});

export const GetCategorizedTasks = createAsyncThunk('projectData/getCategorizedTasks', async (filters = {}, { rejectWithValue }) => {
    try {
        const params = new URLSearchParams();

        if (filters.workstream_repr) {
            params.append('workstream_repr', filters.workstream_repr);
        }

        if (filters.status) {
            params.append('status', filters.status);
        }

        const queryString = params.toString();
        const url = queryString ? `${CATEGORIZED_TASKS}?${queryString}` : CATEGORIZED_TASKS;

        const data = await axiosInstance.get(url);
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});

const checkStatus = selectedData => {
    if (isObject(selectedData) && Object?.keys(selectedData)?.length > 0) {
        if (
            !selectedData?.logo ||
            !selectedData?.deadline ||
            !selectedData?.website ||
            !selectedData?.sector ||
            !selectedData?.investment_type ||
            !selectedData?.customer_type ||
            !selectedData?.customer_stage ||
            selectedData?.revenue == 0 ||
            !selectedData?.description
        ) {
            return 'pending';
        } else {
            return 'completed';
        }
    }
};
// Helper function to get initial view preference safely
const getInitialViewPreference = () => {
    try {
        return localStorage.getItem('projectDashboardView') || 'kanban';
    } catch (error) {
        return 'kanban';
    }
};

export const projectDataSlice = createSlice({
    name: 'projectDataSlice',
    initialState: {
        loading: true,
        logo: '',
        data: [],
        logoLoading: true,
        status: 'pending',
        errorMessage: '',
        selectedProject: null,
        error: false,
        projectDetailButtonLoading: false,
        companyProfileFetchStatus: 'idle',
        ndaButtonLoading: false,
        projectDashboardView: getInitialViewPreference(), // Safe initialization
        categorizedTasks: {
            data: null,
            loading: false,
            error: null,
            filters: {
                workstream_repr: '',
                status: '',
            },
        },
        duplicateJobs: [],
    },
    reducers: {
        registerDuplicateJob: (state, { payload }) => {
            if (!Array.isArray(state.duplicateJobs)) {
                state.duplicateJobs = [];
            }
            if (!payload?.jobId) return;
            const existing = state.duplicateJobs.find(job => job.jobId === payload.jobId);
            if (existing) {
                Object.assign(existing, payload);
                return;
            }
            state.duplicateJobs.push({
                jobId: payload.jobId,
                sourceProjectId: payload.sourceProjectId,
                projectName: payload.projectName,
                status: payload.status || 'pending',
                sourceProjectSnapshot: payload.sourceProjectSnapshot || {},
            });
        },
        updateDuplicateJobStatus: (state, { payload }) => {
            const job = state.duplicateJobs.find(item => item.jobId === payload.jobId);
            if (job && payload.status) {
                job.status = payload.status;
            }
        },
        completeDuplicateJob: (state, { payload }) => {
            state.duplicateJobs = state.duplicateJobs.filter(job => job.jobId !== payload.jobId);
        },
        removeDuplicateJob: (state, { payload }) => {
            state.duplicateJobs = state.duplicateJobs.filter(job => job.jobId !== payload.jobId);
        },
        applySelectedProjectDetail: (state, { payload }) => {
            if (!payload?.id) {
                return;
            }
            localStorage.setItem('project_id', payload.id);
            state.selectedProject = payload;
            state.status = checkStatus(payload);
            const list = Array.isArray(state.data) ? state.data : [];
            const index = list.findIndex(i => String(i.id) === String(payload.id));
            if (index >= 0) {
                state.data[index] = payload;
            } else {
                state.data = [...list, payload];
            }
        },
        selectProject: (state, { payload }) => {
            if (!payload?.id) {
                state.selectedProject = null;
                return;
            }
            localStorage.setItem('project_id', payload.id);
            const index = state?.data?.findIndex(i => String(i.id) === String(payload.id));
            const selectedData = index >= 0 ? state?.data[index] : null;
            if (!selectedData) {
                return;
            }
            state.selectedProject = selectedData;
            state.status = checkStatus(selectedData);
            if (payload?.callback) {
                payload?.callback();
                // if (payload?.userType === 'Investor') {
                //     if (selectedData?.progress_check?.projectscope_pending === true && selectedData?.progress_check?.projectscope_approved === false) {
                //         payload?.callback(true);
                //     } else payload?.callback(false);
                // } else {
                //     if (selectedData?.progress_check?.projectscope_pending === false && selectedData?.progress_check?.projectscope_approved === false) {
                //         payload?.callback(true);
                //     } else payload?.callback(false);
                // }
            }
        },
        changeProjectDeal: (state, { payload }) => {
            state.data = state.data?.map(item => {
                if (item?.id === localStorage['project_id']) {
                    return {
                        ...item,
                        progress_check: {
                            ...item?.progress_check,
                            project_status: payload,
                        },
                    };
                } else return item;
            });
            state.selectedProject = {
                ...state.selectedProject,
                progress_check: {
                    ...state.selectedProject?.progress_check,
                    project_status: payload,
                },
            };
        },

        updateProject: (state, { payload }) => {
            const i = state?.data?.findIndex(_element => _element.id === payload?.id);
            if (i > -1) {
                const UpdatedData = state?.data.map(obj => {
                    if (obj?.id === payload?.id) {
                        return { ...obj, ...payload };
                    }
                    return obj;
                });
                UpdatedData?.sort((a, b) => (a?.project_name > b?.project_name ? 1 : b?.project_name > a?.project_name ? -1 : 0));
                return {
                    ...state,
                    status: checkStatus(payload),
                    selectedProject: payload,
                    data: [...UpdatedData],
                };
            } else {
                return {
                    ...state,
                    selectedProject: payload,
                    status: checkStatus(payload),
                    data: [...state.data, payload],
                };
            }
        },
        setProjectImg: (state, { payload }) => {
            return { ...state, logo: payload, logoLoading: true };
        },
        resetProjectData: (state, { payload }) => {
            return {
                loading: true,
                logo: '',
                // data: [],
                logoLoading: true,
                status: 'pending',
                errorMessage: '',
                selectedProject: null,
                error: false,
                projectDetailButtonLoading: false,
                ndaButtonLoading: false,
                data: state?.data,
                projectDashboardView: state?.projectDashboardView || getInitialViewPreference(),
                categorizedTasks: state?.categorizedTasks || {
                    data: null,
                    loading: false,
                    error: null,
                    filters: {
                        workstream_repr: '',
                        status: '',
                    },
                },
            };
        },
        setProjectDashboardView: (state, { payload }) => {
            state.projectDashboardView = payload;
            // Persist to localStorage
            localStorage.setItem('projectDashboardView', payload);
        },
        setCategorizedTasksFilters: (state, { payload }) => {
            state.categorizedTasks.filters = { ...state.categorizedTasks.filters, ...payload };
        },
        clearCategorizedTasks: state => {
            state.categorizedTasks.data = null;
            state.categorizedTasks.error = null;
        },
        // Migration: Ensure categorizedTasks exists
        ensureCategorizedTasks: state => {
            if (!state.categorizedTasks) {
                state.categorizedTasks = {
                    data: null,
                    loading: false,
                    error: null,
                    filters: {
                        workstream_repr: '',
                        status: '',
                    },
                };
            }
        },
    },
    extraReducers: {
        [REHYDRATE]: (state, action) => {
            if (!Array.isArray(state.duplicateJobs)) {
                state.duplicateJobs = action.payload?.projectData?.duplicateJobs ?? [];
            }
        },
        [GetProjectData.fulfilled]: (state, { payload }) => {
            state.data = payload?.data;
            if (payload?.data?.length > 0) {
                const sortedata = (state.data = payload?.data?.sort((a, b) => (a?.project_name > b?.project_name ? 1 : b?.project_name > a?.project_name ? -1 : 0)));
                state.data = sortedata;
            }
            state.error = false;
            state.errorMessage = '';
            state.loading = false;
        },
        [GetProjectData.rejected]: (state, { error }) => {
            state.error = true;
            state.errorMessage = error?.message;
            state.data = [];
            state.loading = false;
        },
        [FetchLogo.fulfilled]: (state, { payload }) => {
            state.logoLoading = false;
            state.logo = payload?.data?.image;
        },
        [FetchLogo.rejected]: (state, action) => {
            state.logoLoading = false;
            state.isError = true;
            state.logo = '';
            state.errorMessage = action;
        },
        //DeleteProjectData
        [DeleteProjectData.fulfilled]: (state, action) => {
            toast.success('Deleted Successfully');
            state.data = state.data?.filter(item => {
                if (item?.id !== action?.meta?.arg?.id) {
                    return item;
                }
            });
        },
        //GetCurrentProjectData
        [GetCurrentProjectData.fulfilled]: (state, action) => {
            const detail = action?.payload?.data;
            if (!detail?.id) {
                return;
            }
            state.selectedProject = detail;
            if (Array.isArray(state.data)) {
                const index = state.data.findIndex(item => String(item.id) === String(detail.id));
                if (index >= 0) {
                    state.data[index] = { ...state.data[index], ...detail };
                }
            }
        },
        //UpdateProjectProgress - intentionally does not update Redux state
        [UpdateProjectProgress.fulfilled]: (state, action) => {
            // This action only refreshes the project progress on the server side
            // without updating the Redux state to avoid unnecessary re-renders
        },
        //PatchProjectData
        [PatchProjectData.pending]: (state, action) => {
            state.projectDetailButtonLoading = true;
        },
        [PatchProjectData.fulfilled]: (state, { meta, payload }) => {
            console.log(meta?.arg);
            if ('archive' in meta?.arg?.data) {
                toast.success(meta?.arg?.data?.archive ? 'Project Archived' : 'Project Unarchived');
            } else if ('soft_delete' in meta?.arg?.data) {
                toast.success('Project Deleted');
            } else if (meta?.arg?.governance_index_state || meta?.arg?.maturity_index_state || meta?.arg?.security_index_state) {
                //do nothing
            } else if (!meta?.arg?.suppressSuccessToast) {
                toast.success('Project Saved');
            }
            if (state.selectedProject) {
                state.selectedProject = payload?.data;
            }
            state.projectDetailButtonLoading = false;
            state.data = state.data?.map(item => {
                if (item?.id === payload?.data?.id) return payload?.data;
                else return item;
            });
        },
        [PatchProjectData.rejected]: (state, { payload }) => {
            if (payload && typeof payload === 'object') {
                Object.keys(payload).forEach(key => {
                    if (Array.isArray(payload[key])) {
                        payload[key].forEach(msg => toast.error(capitalizeFirstLetter(key) + ' : ' + msg));
                    } else if (payload[key]) {
                        toast.error(capitalizeFirstLetter(key) + ' : ' + payload[key]);
                    }
                });
            }
            state.projectDetailButtonLoading = false;
        },
        // PostProjectData
        [PostProjectData.pending]: (state, action) => {
            state.projectDetailButtonLoading = true;
        },
        [PostProjectData.fulfilled]: (state, { payload, meta }) => {
            if (!meta?.arg?.suppressSuccessToast) {
                toast.success('Project Created');
            }
            localStorage.setItem('project_id', payload?.data?.id);
            if (!meta?.arg?.keepLoadingUntilMerge) {
                state.projectDetailButtonLoading = false;
            }
            state.data.push(payload?.data);
        },
        [PostProjectData.rejected]: (state, { payload, meta }) => {
            if (payload && typeof payload === 'object') {
                Object.keys(payload).forEach(key => {
                    if (Array.isArray(payload[key])) {
                        payload[key].forEach(msg => toast.error(capitalizeFirstLetter(key) + ' : ' + msg));
                    } else if (payload[key]) {
                        toast.error(capitalizeFirstLetter(key) + ' : ' + payload[key]);
                    }
                });
            }
            state.projectDetailButtonLoading = false;
        },
        [DuplicateProjectData.pending]: state => {
            state.projectDetailButtonLoading = true;
        },
        [DuplicateProjectData.fulfilled]: state => {
            state.projectDetailButtonLoading = false;
        },
        [DuplicateProjectData.rejected]: (state, { payload }) => {
            state.projectDetailButtonLoading = false;
            if (payload?.detail) {
                toast.error(payload.detail);
            } else if (payload && typeof payload === 'object') {
                Object.keys(payload).forEach(key => {
                    if (Array.isArray(payload[key])) {
                        payload[key].forEach(msg => toast.error(`${capitalizeFirstLetter(key)}: ${msg}`));
                    } else if (payload[key]) {
                        toast.error(`${capitalizeFirstLetter(key)}: ${payload[key]}`);
                    }
                });
            } else {
                toast.error('Project duplication failed.');
            }
        },
        [GetMergedCompanyProfile.pending]: (state, action) => {
            state.companyProfileFetchStatus = 'pending';
            const background = typeof action.meta.arg === 'object' && action.meta.arg?.background;
            if (!background) {
                state.projectDetailButtonLoading = true;
            }
        },
        [GetMergedCompanyProfile.fulfilled]: (state, action) => {
            state.companyProfileFetchStatus = 'fulfilled';
            const background = typeof action.meta.arg === 'object' && action.meta.arg?.background;
            if (!background) {
                state.projectDetailButtonLoading = false;
            }
        },
        [GetMergedCompanyProfile.rejected]: (state, action) => {
            state.companyProfileFetchStatus = 'rejected';
            const background = typeof action.meta.arg === 'object' && action.meta.arg?.background;
            if (!background) {
                state.projectDetailButtonLoading = false;
            }
        },
        //AddNda
        [AddNda.pending]: (state, action) => {
            state.status = 'loading';
            state.ndaButtonLoading = true;
        },
        [AddNda.fulfilled]: (state, action) => {
            toast.success('NDA Added');
            state.status = 'success';
            state.ndaButtonLoading = false;
        },
        [AddNda.rejected]: (state, { payload }) => {
            state.ndaButtonLoading = false;
            if (payload && typeof payload === 'object') {
                Object.keys(payload).forEach(key => {
                    if (Array.isArray(payload[key])) {
                        payload[key].forEach(msg => toast.error(key + ' : ' + msg));
                    } else if (payload[key]) {
                        toast.error(key + ' : ' + payload[key]);
                    }
                });
            }
            state.status = 'rejected';
        },
        //DeleteNda
        [DeleteNda.pending]: (state, action) => {
            state.status = 'loading';
            state.ndaButtonLoading = true;
        },
        [DeleteNda.fulfilled]: (state, action) => {
            toast.success('NDA Deleted');
            state.status = 'success';
            state.ndaButtonLoading = false;
        },
        [DeleteNda.rejected]: (state, { payload }) => {
            state.ndaButtonLoading = false;
            try {
                if (payload && typeof payload === 'object') {
                    Object.keys(payload).forEach(key => {
                        if (Array.isArray(payload[key])) {
                            payload[key].forEach(msg => toast.error(key + ' : ' + msg));
                        } else if (payload[key]) {
                            toast.error(key + ' : ' + payload[key]);
                        }
                    });
                }
            } catch (error) {
                console.log(error);
            }

            state.status = 'rejected';
        },
        //GetCategorizedTasks
        [GetCategorizedTasks.pending]: state => {
            state.categorizedTasks.loading = true;
            state.categorizedTasks.error = null;
        },
        [GetCategorizedTasks.fulfilled]: (state, { payload }) => {
            state.categorizedTasks.loading = false;
            state.categorizedTasks.data = payload?.data;
            state.categorizedTasks.error = null;
        },
        [GetCategorizedTasks.rejected]: (state, { payload }) => {
            state.categorizedTasks.loading = false;
            state.categorizedTasks.error = payload;
        },
    },
});

export const {
    selectProject,
    applySelectedProjectDetail,
    updateProject,
    setProjectImg,
    changeProjectDeal,
    resetProjectData,
    setProjectDashboardView,
    setCategorizedTasksFilters,
    clearCategorizedTasks,
    ensureCategorizedTasks,
    registerDuplicateJob,
    updateDuplicateJobStatus,
    completeDuplicateJob,
    removeDuplicateJob,
} = projectDataSlice.actions;
export default projectDataSlice.reducer;
